import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  DatabaseZap,
  Loader2,
  Download,
  CheckCircle2,
  AlertTriangle,
  Plus,
  RefreshCw,
} from "lucide-react";
import {
  catalogSeedPreview,
  catalogSeedApply,
  catalogBackupDownload,
} from "@/lib/api";

const Stat = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "text-white",
    create: "text-[#2EE59D]",
    update: "text-[#66B2FF]",
    muted: "text-white/50",
    warn: "text-[#F7C948]",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
      <div className="text-white/45 text-[11px] uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
};

export const CatalogMaintenance = ({ token, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const openDialog = async () => {
    setResult(null);
    setPreview(null);
    setOpen(true);
    setLoading(true);
    try {
      setPreview(await catalogSeedPreview(token));
    } catch {
      toast.error("Failed to load catalog preview");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const summary = await catalogSeedApply(token);
      setResult(summary);
      toast.success("Catalog updated successfully");
      onApplied && onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to apply catalog seed");
    } finally {
      setApplying(false);
    }
  };

  const downloadBackup = async () => {
    if (!result?.backup_id) return;
    try {
      const res = await catalogBackupDownload(token, result.backup_id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rims-catalog-backup-${result.backup_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Backup download failed");
    }
  };

  return (
    <div
      className="rounded-xl border border-white/10 p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      style={{ backgroundColor: "#0F1424" }}
      data-testid="catalog-maintenance-card"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-[#1E6BFF]/15 border border-[#1E6BFF]/30 p-2.5">
          <DatabaseZap size={20} className="text-[#66B2FF]" />
        </div>
        <div>
          <div className="text-white font-semibold">Seed / Update Recommended Role Catalog</div>
          <p className="text-white/45 text-sm mt-0.5 max-w-xl">
            Bulk-create or update the recommended roles and industries. A backup is taken
            automatically before any change, and existing records are preserved.
          </p>
        </div>
      </div>
      <Button
        data-testid="catalog-seed-open-button"
        onClick={openDialog}
        className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg h-11 shrink-0"
      >
        <RefreshCw size={16} className="mr-2" /> Review &amp; Apply
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl bg-[#0F1424] border-white/10 text-white max-h-[90vh] overflow-y-auto"
          data-testid="catalog-seed-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Seed / Update Recommended Role Catalog
            </DialogTitle>
            <DialogDescription className="text-white/45">
              Review the changes below. Nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
            </div>
          ) : result ? (
            <div className="space-y-4" data-testid="catalog-seed-result">
              <div className="flex items-center gap-2 text-[#2EE59D]">
                <CheckCircle2 size={18} /> <span className="font-medium">Catalog updated</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Roles created" value={result.created?.role_count ?? 0} tone="create" />
                <Stat label="Roles updated" value={result.updated?.role_count ?? 0} tone="update" />
                <Stat label="Total roles" value={result.total_roles ?? 0} />
                <Stat label="Industries created" value={result.created?.industry_count ?? 0} tone="create" />
                <Stat label="Industries updated" value={result.updated?.industry_count ?? 0} tone="update" />
                <Stat label="Total industries" value={result.total_industries ?? 0} />
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3 text-sm">
                <div className="flex items-center gap-2">
                  {(result.validation_after || []).length === 0 ? (
                    <span className="text-[#2EE59D] flex items-center gap-1">
                      <CheckCircle2 size={15} /> Validation passed — every active industry has a viable recommendation path.
                    </span>
                  ) : (
                    <span className="text-[#F7C948] flex items-center gap-1">
                      <AlertTriangle size={15} /> {(result.validation_after || []).length} validation warning(s)
                    </span>
                  )}
                </div>
                {(result.validation_after || []).map((iss, i) => (
                  <div key={i} className="text-white/60 text-xs mt-1">• {iss}</div>
                ))}
              </div>
              <Button
                data-testid="catalog-download-backup-button"
                onClick={downloadBackup}
                variant="secondary"
                className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-lg"
              >
                <Download size={15} className="mr-2" /> Download pre-change backup
              </Button>
            </div>
          ) : preview ? (
            <div className="space-y-4" data-testid="catalog-seed-preview">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Roles to create" value={preview.roles?.create_count ?? 0} tone="create" />
                <Stat label="Roles to update" value={preview.roles?.update_count ?? 0} tone="update" />
                <Stat label="Roles unchanged" value={preview.roles?.unchanged_count ?? 0} tone="muted" />
                <Stat label="Industries to create" value={preview.industries?.create_count ?? 0} tone="create" />
                <Stat label="Industries to update" value={preview.industries?.update_count ?? 0} tone="update" />
                <Stat label="Industries unchanged" value={preview.industries?.unchanged_count ?? 0} tone="muted" />
              </div>

              {preview.roles?.create?.length > 0 && (
                <div>
                  <div className="text-white/60 text-sm mb-2 flex items-center gap-1">
                    <Plus size={14} /> New roles ({preview.roles.create.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.roles.create.map((r) => (
                      <Badge key={r.key} className="border bg-[#2EE59D]/10 text-[#2EE59D] border-[#2EE59D]/25">
                        {r.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {preview.conflicts?.length > 0 && (
                <div className="rounded-lg border border-[#F7C948]/30 bg-[#F7C948]/10 px-4 py-3">
                  <div className="text-[#F7C948] text-sm flex items-center gap-1 mb-1">
                    <AlertTriangle size={15} /> Conflicts requiring your decision
                  </div>
                  {preview.conflicts.map((c, i) => (
                    <div key={i} className="text-white/70 text-xs">
                      Duplicate name “{c.name}”: {c.keys.join(", ")}
                    </div>
                  ))}
                </div>
              )}

              {preview.deactivation_candidates?.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-[#0A0F1E] px-4 py-3">
                  <div className="text-white/70 text-sm mb-1">Proposed deactivations</div>
                  {preview.deactivation_candidates.map((d, i) => (
                    <div key={i} className="text-white/60 text-xs">
                      {d.name} — {d.safe_to_deactivate ? "safe (unreferenced)" : "referenced by history (kept)"}
                    </div>
                  ))}
                </div>
              )}

              <div className="text-white/40 text-xs">
                {preview.referenced_role_count} role(s) referenced by historical assessments will be preserved.
              </div>
            </div>
          ) : null}

          {!result && !loading && (
            <DialogFooter className="mt-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                data-testid="catalog-seed-confirm-button"
                onClick={apply}
                disabled={applying}
                className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
              >
                {applying ? <Loader2 className="animate-spin mr-2" size={15} /> : null}
                Confirm &amp; Apply
              </Button>
            </DialogFooter>
          )}

          {result && (
            <DialogFooter className="mt-2">
              <Button
                onClick={() => setOpen(false)}
                className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
              >
                Done
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
